import type {DashboardTicket} from '../types/dashboardTicket';
import {fromGlobalId} from '../../tickets/utils/fromGlobalId';
import {toTicketLabel} from '../../tickets/utils/labelDisplay';
import type {useDashboardTicketsQuery_tickets$data} from '../hooks/__generated__/useDashboardTicketsQuery_tickets.graphql';

type DashboardEdges = NonNullable<useDashboardTicketsQuery_tickets$data['tickets']['edges']>;
export type DashboardTicketNode = NonNullable<NonNullable<DashboardEdges[number]>['node']>;

/**
 * Maps one GraphQL ticket node from the dashboard connection to the
 * {@link DashboardTicket} shape the table, grouping and CSV export consume.
 *
 * Fields with no backing data from the current query are mapped to `null` so
 * the table renders the em-dash placeholder rather than crashing or going blank.
 */
export function dashboardTicketFromNode(node: DashboardTicketNode): DashboardTicket {
	return {
		id: fromGlobalId(node.id),
		slug: node.slug,
		subject: node.subject,
		status: node.status as DashboardTicket['status'],
		priority: node.priority as DashboardTicket['priority'],
		channel: (node.channel ?? 'manual') as DashboardTicket['channel'],
		created_at: node.createdAt as string,
		resolved_at: (node.resolvedAt as string | null) ?? null,
		last_activity_at: (node.lastActivityAt as string | null) ?? null,
		resolution_time_seconds: node.resolutionTimeSeconds ?? null,
		first_response_time_seconds: node.firstResponseTimeSeconds ?? null,
		requester_name: node.requester?.name ?? null,
		assignee_name: node.assignee?.fullName ?? null,
		// Grouping keys on an id where one exists and falls back to the display
		// name where it does not, so carrying the ids through keeps two people who
		// share a name in separate buckets — and keeps a requester known by email
		// alone out of the "unknown requester" bucket.
		//
		// Both ids arrive as Relay global ids and are decoded to the raw entity id,
		// which is also the id the server's per-group rollups key their buckets by:
		// leaving either encoded would mean no group header could ever be matched to
		// its server sub-total.
		requester_id: node.requesterId ? fromGlobalId(node.requesterId) : null,
		requester_email: node.requester?.email ?? null,
		assignee_id: node.assigneeId ? fromGlobalId(node.assigneeId) : null,
		labels: node.labels.map(toTicketLabel),
		sla: node.sla
			? {
					breached: node.sla.breached,
					elapsedSeconds: node.sla.elapsedSeconds,
					thresholdSeconds: node.sla.thresholdSeconds,
					paused: node.sla.paused,
					// Timestamps are not exposed by this query; columns hold their
					// place with an em-dash until a separate issue adds them.
					due_at: null,
					breached_at: null,
					paused_at: null,
			  }
			: null,
		// Not backed by the ticket connection — columns render em-dash until
		// separate issues add each field.
		contact_name: null,
		sla_policy_name: null,
		project: null,
		time_in_current_status_seconds: null,
		watchers_count: null,
	};
}
